import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

/** Wartet, bis das Google-Identity-Skript aus index.html geladen ist. */
function warteAufGoogle(timeoutMs = 10000) {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve(window.google);
    const start = Date.now();
    const timer = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(timer);
        resolve(window.google);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(timer);
        reject(new Error("Google-Anmeldedienst nicht erreichbar."));
      }
    }, 50);
  });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Die Pruefung beim Start bekommt eine kurze Frist. Die allgemeine
  // Zeitgrenze von api.js (drei Minuten) ist fuer Auswertungen gedacht; hier
  // hiesse sie: drei Minuten Ladekreisel auf hellem Grund, von aussen eine
  // weisse Seite (iPhone, Safari 26, 25.09.2026). Nach 15 Sekunden ohne
  // Antwort gilt "nicht angemeldet" - dann kommt die Anmeldeseite, und der
  // Knopf dort loest die naechste Anfrage aus. Der Stand steht in
  // window.__crmStart, damit der Waechter in index.html ihn hinschreiben kann.
  const checkAuth = useCallback(async () => {
    window.__crmStart = { auth: "laeuft" };
    // Die Frist liegt AUSSEN um die Anfrage (Promise.race), nicht nur als
    // timeout der Anfrage: Haengt die Verbindung schon vor dem Absenden, zaehlt
    // der Browser die Anfrage-Frist gar nicht erst - in WebKit nachgestellt.
    let wecker;
    const frist = new Promise((_, ablehnen) => {
      wecker = setTimeout(() => ablehnen(Object.assign(new Error("Anmeldepruefung ohne Antwort nach 15 s"), { code: "FRIST" })), 15000);
    });
    try {
      const res = await Promise.race([api.get("/auth/me", { timeout: 15000 }), frist]);
      setUser(res.data);
      window.__crmStart = { auth: "angemeldet" };
    } catch (err) {
      setUser(null);
      window.__crmStart = { auth: err?.response ? `HTTP ${err.response.status}` : (err?.code || err?.message || "keine Antwort") };
    } finally {
      clearTimeout(wecker);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /** Tauscht das Google-ID-Token gegen unser Sitzungs-Cookie. */
  const handleCredential = useCallback(async (response) => {
    setAuthError(null);
    try {
      const res = await api.post("/auth/session", { credential: response.credential });
      setUser(res.data);
      // Vom Anmeldebildschirm aktiv wegnavigieren. Das Setzen von user allein
      // genügt nicht, wenn die Route /login unabhängig davon gerendert bleibt.
      if (window.location.pathname === "/login") {
        window.location.replace("/");
      }
    } catch (err) {
      setUser(null);
      setAuthError(
        err.response?.data?.detail || "Anmeldung fehlgeschlagen. Bitte erneut versuchen."
      );
    }
  }, []);

  /**
   * Rendert den offiziellen Google-Anmeldebutton in das uebergebene Element.
   * Zuverlaessiger als One Tap, das der Browser unterdruecken kann.
   */
  const renderGoogleButton = useCallback(
    async (element) => {
      if (!element) return;
      if (!CLIENT_ID) {
        setAuthError("REACT_APP_GOOGLE_CLIENT_ID ist nicht gesetzt.");
        return;
      }
      try {
        const google = await warteAufGoogle();
        google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: handleCredential,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        element.innerHTML = "";
        google.accounts.id.renderButton(element, {
          theme: "filled_black",
          size: "large",
          width: 320,
          text: "signin_with",
          locale: "de",
        });
      } catch (err) {
        setAuthError(err.message);
      }
    },
    [handleCredential]
  );

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignorieren */
    }
    try {
      window.google?.accounts?.id?.disableAutoSelect();
    } catch {
      /* ignorieren */
    }
    setUser(null);
    window.location.href = "/login";
  };

  // Das Konto des Wandbildschirms. Es darf serverseitig ohnehin nur das
  // Wallboard; hier steht es, damit die Oberfläche es gar nicht erst
  // woandershin schickt – sonst sähe man eine Seite, auf der jede Anfrage
  // mit 403 endet.
  const istWallboard = !!user && user.role === "wallboard";

  /**
   * Vorschau: die Anwendung so sehen wie jemand ohne Umsatzrechte.
   *
   * Wer entscheidet, welche Zahlen ein Mitarbeiter sehen darf, sollte
   * nachsehen können, was dabei herauskommt. Sonst richtet man eine Ansicht
   * ein, die man selbst nie zu Gesicht bekommt.
   *
   * Die Vorschau nimmt nur weg, sie gibt nichts frei: Sie setzt canRevenue
   * auf false und blendet damit dieselben Spalten aus, die auch der Server
   * nicht liefern würde. Ein Sicherheitsmerkmal ist sie nicht und soll es
   * nicht sein – die Rechte prüft weiterhin allein der Server.
   *
   * Sie überlebt einen Seitenwechsel (sessionStorage), aber nicht das
   * Schließen des Fensters. Beim Start des Programms soll niemand in einer
   * Ansicht sitzen, die er vor drei Tagen zum Ausprobieren eingeschaltet hat.
   */
  const [vorschauOhneUmsatz, setVorschauOhneUmsatz] = useState(() => {
    try {
      return sessionStorage.getItem("vorschau") === "ohne-umsatz";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      if (vorschauOhneUmsatz) sessionStorage.setItem("vorschau", "ohne-umsatz");
      else sessionStorage.removeItem("vorschau");
    } catch {
      // Privates Fenster o.Ä. – dann gilt die Vorschau nur für diese Seite.
    }
  }, [vorschauOhneUmsatz]);

  // Auch über die Adresse einschaltbar, damit man den Blick per Link teilen
  // oder als Lesezeichen ablegen kann: ...?sicht=ohne-umsatz
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("sicht");
    if (p === "ohne-umsatz") setVorschauOhneUmsatz(true);
    if (p === "normal") setVorschauOhneUmsatz(false);
  }, []);

  const darfUmsatz = !!user && !istWallboard
    && (user.role === "owner" || user.can_view_revenue === true);
  const canRevenue = darfUmsatz && !vorschauOhneUmsatz;
  // Zwei Verwaltungsstufen: Superadmin darf alles wie der Owner (ausser
  // Owner ernennen), Admin verwaltet - sieht aber nur die Funktionen, die
  // ihm ein Superadmin nicht ausgeblendet hat.
  const isSuperadmin = !!user && (user.role === "owner" || user.role === "superadmin");
  const canManage = isSuperadmin || (!!user && user.role === "admin");
  // Freigeschaltete Bereiche (z. B. "wettbewerb"). Der Server rechnet die
  // Liste fertig aus (Haupt-Owner immer, sonst nur mit Freigabe) - hier wird
  // nur nachgeschlagen, entscheiden tut weiterhin allein der Server.
  const darfBereich = (name) => !!user && !istWallboard
    && (user.bereiche_wirksam || []).includes(name);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        authError,
        checkAuth,
        renderGoogleButton,
        logout,
        canRevenue,
        canManage,
        isSuperadmin,
        darfBereich,
        istWallboard,
        // Für den Hinweisbalken und den Schalter: Wer die Rechte hätte,
        // sie aber gerade zur Probe weggelegt hat.
        darfUmsatz,
        vorschauOhneUmsatz,
        setVorschauOhneUmsatz,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
